import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './TopPerformers.css';

// ============================================================================
// FEATURE FLAG: Toggle "View Open Trades" functionality
// Set to 'false' to disable the modal and button
// Set to 'true' to enable the modal and button
// ============================================================================
const ENABLE_VIEW_OPEN_TRADES = false;

function TopPerformers({ guruSlug }) {
  const [performers, setPerformers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rankingMode, setRankingMode] = useState('all_time');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productTrades, setProductTrades] = useState([]);
  const [showTradesModal, setShowTradesModal] = useState(false);
  const [scannerProducts, setScannerProducts] = useState([]);
  const [scannersLoading, setScannersLoading] = useState(true);

  useEffect(() => {
    fetchTopPerformers();
    fetchScannerProducts();
  }, [guruSlug, rankingMode]);

  const fetchTopPerformers = async () => {
    try {
      // Always show loading state when fetching (including tab switches)
      setLoading(true);

      const response = await api.get(`/gurus/${guruSlug}/top-performers`, {
        params: { ranking_mode: rankingMode, limit: 3 }
      });
      setPerformers(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching top performers:', err);
      setError('Failed to load top performers');
      setLoading(false);
    }
  };

  const fetchScannerProducts = async () => {
    try {
      setScannersLoading(true);
      const response = await api.get(`/gurus/${guruSlug}/products`);
      // Filter for products with Trade Tracker enabled (have sheet_url)
      const trackingProducts = response.data.filter(p => p.sheet_url);
      setScannerProducts(trackingProducts);
      setScannersLoading(false);
    } catch (err) {
      console.error('Error fetching scanner products:', err);
      setScannersLoading(false);
    }
  };

  const handleCardClick = async (product) => {
    // If View Open Trades is disabled, do nothing
    if (!ENABLE_VIEW_OPEN_TRADES) {
      return;
    }

    try {
      setSelectedProduct(product);
      // Fetch OPEN trades for this product
      const response = await api.get(`/gurus/${guruSlug}/trades`, {
        params: {
          product_id: product.product_id,
          status: 'open'
        }
      });
      setProductTrades(response.data);
      setShowTradesModal(true);
    } catch (err) {
      console.error('Error fetching product trades:', err);
    }
  };

  const getMedalEmoji = (rank) => {
    switch(rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    const numValue = parseFloat(value);
    const sign = numValue >= 0 ? '+' : '';
    return `${sign}${numValue.toFixed(2)}%`;
  };

  if (error) {
    return <div className="top-performers-error">{error}</div>;
  }

  const getRankingLabel = () => {
    switch(rankingMode) {
      case 'last_3_months': return 'Last 3 Months';
      case 'last_1_month': return 'Last 1 Month';
      case 'last_1_week': return 'Last 1 Week';
      default: return 'All Time';
    }
  };

  return (
    <div className="top-performers-container">
      <div className="top-performers-left">
        <div className="top-performers-header">
          <div className="header-content">
            <h2 className="section-title">
              Top Performing Strategies
            </h2>
            <div className="ranking-toggle">
              <button
                type="button"
                className={`toggle-btn ${rankingMode === 'all_time' ? 'active' : ''}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRankingMode('all_time'); }}
              >
                All Time
              </button>
              <button
                type="button"
                className={`toggle-btn ${rankingMode === 'last_3_months' ? 'active' : ''}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRankingMode('last_3_months'); }}
              >
                Last 3 Months
              </button>
              <button
                type="button"
                className={`toggle-btn ${rankingMode === 'last_1_month' ? 'active' : ''}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRankingMode('last_1_month'); }}
              >
                Last 1 Month
              </button>
              <button
                type="button"
                className={`toggle-btn ${rankingMode === 'last_1_week' ? 'active' : ''}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRankingMode('last_1_week'); }}
              >
                Last 1 Week
              </button>
              <button
                type="button"
                className={`toggle-btn ${rankingMode === 'last_1_year' ? 'active' : ''}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRankingMode('last_1_year'); }}
              >
                Last 1 Year
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="top-performers-loading">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <div className="loading-text">
              <span>Loading {getRankingLabel()} rankings</span>
              <span className="loading-dots">
                <span className="dot">.</span>
                <span className="dot">.</span>
                <span className="dot">.</span>
              </span>
            </div>
          </div>
        ) : performers.length === 0 ? (
          <div className="no-performers">
            <p>No performance data available for this period.</p>
          </div>
        ) : (
          <div className="performers-grid">
            {performers.map((performer) => (
            <div
              key={performer.product_id}
              className={`performer-card-module rank-${performer.rank} ${!ENABLE_VIEW_OPEN_TRADES ? 'non-clickable' : ''}`}
              onClick={ENABLE_VIEW_OPEN_TRADES ? () => handleCardClick(performer) : undefined}
            >
              <div className="rank-badge-corner">
                <span className="medal-large">{getMedalEmoji(performer.rank)}</span>
              </div>

              {performer.product_logo_url ? (
                <img src={performer.product_logo_url} alt={performer.product_name} className="performer-logo" />
              ) : (
                <div className="performer-logo-placeholder">
                  {performer.product_name.substring(0, 2).toUpperCase()}
                </div>
              )}

              <h3 className="performer-product-name">{performer.product_name}</h3>

              <div className="performer-stats">
                <div className="performer-avg-return">
                  <div className="stat-label">Avg Return</div>
                  <div className={`stat-value ${parseFloat(performer.average_return) >= 0 ? 'positive' : 'negative'}`}>
                    {formatPercent(performer.average_return)}
                  </div>
                </div>

                <div className="performer-win-rate">
                  <div className="stat-label">Win Rate</div>
                  <div className="stat-value">{parseFloat(performer.win_rate).toFixed(2)}%</div>
                </div>

                <div className="performer-record">
                  <div className="stat-label">Record</div>
                  <div className="stat-value">
                    <span className="winners">{performer.winning_trades}W</span>
                    {' / '}
                    <span className="losers">{performer.losing_trades}L</span>
                  </div>
                </div>
              </div>

              {/* Hidden: Open trades count
              <div className="performer-open-trades-badge">
                {performer.open_trades} Open Trade{performer.open_trades !== 1 ? 's' : ''}
              </div>
              */}

              {ENABLE_VIEW_OPEN_TRADES && (
                <button className="view-open-trades-btn">
                  View Open Trades
                </button>
              )}
            </div>
          ))}
          </div>
        )}

        <div className="recent-alerts-section">
          <div className="alerts-header">
            <h2 className="section-title">Trade Alerts</h2>
          </div>
          {scannersLoading ? (
            <div className="scanners-loading">Loading products...</div>
          ) : scannerProducts.length === 0 ? (
            <div className="no-scanners">
              <p>No tracked products available</p>
            </div>
          ) : (
            <div className="alerts-showcase">
              <div className="overlapping-logos">
                {scannerProducts.map((product, index) => (
                  product.logo_url ? (
                    <div
                      key={product.id}
                      className="showcase-logo-wrapper"
                      style={{ zIndex: scannerProducts.length - index }}
                    >
                      <img
                        src={product.logo_url}
                        alt={product.name}
                        className="showcase-logo"
                        title={product.name}
                      />
                    </div>
                  ) : (
                    <div
                      key={product.id}
                      className="showcase-logo-wrapper"
                      style={{ zIndex: scannerProducts.length - index }}
                    >
                      <div className="showcase-logo-placeholder" title={product.name}>
                        {product.name.substring(0, 2).toUpperCase()}
                      </div>
                    </div>
                  )
                ))}
              </div>
              <a
                href="https://app.oneclicktrading.com/trading/signals"
                target="_blank"
                rel="noopener noreferrer"
                className="oct-alerts-button"
              >
                <span className="oct-button-text">View All Alerts in One Click Trading</span>
                <span className="oct-button-arrow">→</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {ENABLE_VIEW_OPEN_TRADES && showTradesModal && selectedProduct && (
        <div className="trades-modal-overlay" onClick={() => setShowTradesModal(false)}>
          <div className="open-trades-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-simple">
              <div className="modal-header-left">
                <h3>{selectedProduct.product_name}</h3>
                <p className="modal-subtitle">Currently Open Positions</p>
              </div>
              <button className="close-btn" onClick={() => setShowTradesModal(false)}>×</button>
            </div>
            <div className="modal-body-simple">
              {productTrades.length === 0 ? (
                <div className="no-open-trades">
                  <div className="no-trades-icon">📊</div>
                  <p className="no-trades-text">No open trades at the moment</p>
                  <p className="no-trades-subtext">All positions have been closed</p>
                </div>
              ) : (
                <>
                  <div className="open-trades-summary">
                    <div className="summary-badge">
                      <span className="summary-count">{productTrades.length}</span>
                      <span className="summary-label">Open Position{productTrades.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  <div className="open-trades-grid">
                    {productTrades.map((trade, idx) => {
                      // Generate dynamic trade description
                      const daysOpen = trade.entry_date ? Math.floor((new Date() - new Date(trade.entry_date)) / (1000 * 60 * 60 * 24)) : 0;
                      const isStock = trade.asset_type === 'stock';
                      const entryPrice = isStock ? trade.entry_price : trade.option_entry_price;
                      const expiryDate = trade.expiry_date ? new Date(trade.expiry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

                      let tradeDescription = '';

                      // Detect if it's a credit spread
                      const isCreditSpread = trade.option_data && trade.option_data.toLowerCase().includes('credit');
                      const priceLabel = isCreditSpread ? 'premium' : 'entry fill price';

                      if (isStock) {
                        // Stock trade - use buy/sell terminology
                        const action = trade.buy_or_sell === 'sell' ? 'sell' : 'buy';
                        tradeDescription = `This stock position in ${trade.symbol} was opened ${daysOpen} days ago with a ${action} price of ${formatCurrency(entryPrice)} per share.`;
                      } else if (trade.option_type) {
                        // Check if it's a spread first (by checking option_data for spread indicators or multiple strikes)
                        const isSpread = trade.option_data && (
                          trade.option_data.toLowerCase().includes('spread') ||
                          trade.option_data.includes('/') ||
                          trade.option_data.includes('-') ||
                          (trade.option_data.match(/\d+/g) || []).length > 1 // Multiple strikes in option_data
                        );

                        if (isSpread && trade.option_data) {
                          // Spread or complex option
                          const optionType = trade.option_type ? trade.option_type.toUpperCase() : '';
                          if (expiryDate) {
                            tradeDescription = `This ${optionType} spread (${trade.option_data}) on ${trade.symbol} was opened ${daysOpen} days ago with ${isCreditSpread ? 'a' : 'an'} ${priceLabel} of ${formatCurrency(entryPrice)} per spread, expiring on ${expiryDate}.`;
                          } else {
                            tradeDescription = `This ${optionType} spread (${trade.option_data}) on ${trade.symbol} was opened ${daysOpen} days ago with ${isCreditSpread ? 'a' : 'an'} ${priceLabel} of ${formatCurrency(entryPrice)} per spread.`;
                          }
                        } else if (trade.strike_price && expiryDate) {
                          // Simple single option
                          const optionType = trade.option_type.toUpperCase();
                          tradeDescription = `This ${optionType} option on ${trade.symbol} was opened ${daysOpen} days ago with an entry fill price of ${formatCurrency(entryPrice)}, strike price of ${formatCurrency(trade.strike_price)}, and expires on ${expiryDate}.`;
                        } else {
                          // Fallback for options without complete data
                          const optionType = trade.option_type.toUpperCase();
                          tradeDescription = `This ${optionType} option on ${trade.symbol} was opened ${daysOpen} days ago with an entry fill price of ${formatCurrency(entryPrice)}.`;
                        }
                      }

                      return (
                        <div key={trade.id} className="open-trade-card">
                          <div className="trade-card-header">
                            <div className="trade-number-badge">#{idx + 1}</div>
                            <div className="trade-symbol-large">{trade.symbol}</div>
                          </div>

                          <div className="trade-description">
                            <p className="description-text">{tradeDescription}</p>
                          </div>

                          <div className="trade-card-body">
                          {/* Option Type Section */}
                          <div className="trade-info-section">
                            <span className="section-label">Trade Type</span>
                            {trade.option_type ? (
                              <span className={`option-type-badge-large ${trade.option_type.toLowerCase()}`}>
                                {trade.option_type.toUpperCase()}
                              </span>
                            ) : (
                              <span className="detail-value">Stock</span>
                            )}
                          </div>

                          {/* Strike & Expiry Section */}
                          <div className="trade-info-section">
                            <span className="section-label">Strike & Expiry</span>
                            <div className="section-values">
                              {trade.strike_price && (
                                <div className="value-line">
                                  <span className="value-emphasis">{formatCurrency(trade.strike_price)}</span>
                                </div>
                              )}
                              {trade.expiry_date && (
                                <div className="value-line secondary">
                                  Exp: {new Date(trade.expiry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </div>
                              )}
                              {!trade.strike_price && !trade.expiry_date && trade.option_data && (
                                <div className="value-line">{trade.option_data}</div>
                              )}
                            </div>
                          </div>

                          {/* Entry Price Section */}
                          <div className="trade-info-section">
                            <span className="section-label">
                              {isStock
                                ? (trade.buy_or_sell === 'sell' ? 'Sell Price' : 'Buy Price')
                                : (isCreditSpread ? 'Premium' : 'Entry Fill Price')}
                            </span>
                            <div className="value-emphasis price-large">
                              {entryPrice ? formatCurrency(entryPrice) : 'N/A'}
                            </div>
                            {trade.entry_date && (
                              <div className="value-line secondary">
                                {new Date(trade.entry_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                            )}
                          </div>

                          {/* Days Open Badge */}
                          {trade.entry_date && (
                            <div className="trade-info-section">
                              <div className="days-open-badge">
                                <div className="days-number">
                                  {Math.floor((new Date() - new Date(trade.entry_date)) / (1000 * 60 * 60 * 24))}
                                </div>
                                <div className="days-label">Days Open</div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Notes Section - Full Width Below */}
                        {trade.notes && (
                          <div className="trade-notes-section">
                            <span className="notes-label">Execution Notes:</span>
                            <p className="notes-text">{trade.notes}</p>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TopPerformers;
